/**
 * ================================================================================================
 *  הוספת מודול — WHAT THE OFFICE MAY ADD, AND ADDING IT
 * ================================================================================================
 *
 * The Owner, 2026-11-02, naming the tab and then showing the flow he wants from a product he was
 * looking at:
 *
 *     *"האם זה נמצא תחת הגדרות חשבוניות שלי, אמצעי תשלום וביטול מנוי — צריך גם הוספת מודול."*
 *     *"תראה את הדרך (הפלואו) של הוספת מודול, לדוגמא לחצתי על CRM, תראה איך זה מופיע כתצוגה
 *      ומאפשר ללקוח ללחוץ על הפעלה ולחייב לו את כרטיס האשראי."*
 *     *"קונה מיד, בכרטיס שלו."*
 *
 * So: a card per article — headline, what is included, the price with VAT, and one button that
 * says הפעלה.
 *
 * ── THE ONE SENTENCE THIS PANEL MUST NOT GET WRONG ─────────────────────────────────────────────
 *
 * The module OPENS at the moment the button is pressed. **The card is not charged at that
 * moment.** The subscription line is written with today as its first day, `priceCycle` prices it
 * for the days the office held it, and the prorated share lands on the invoice for the period that
 * is already running — `currentPeriodEndsOn` is printed so the sentence names a date rather than
 * implying one. Saying *"וחייב לו את כרטיס האשראי"* about this instant would be the one thing
 * `אין להציג יכולת מדומה כפעילה` forbids, with money attached.
 *
 * ── EVERY JUDGEMENT ON THE SCREEN IS THE SERVER'S ──────────────────────────────────────────────
 *
 * `purchasable` and `blockedBy` are rendered, never recomputed. A browser deciding *"this one is
 * for sale"* out of a price and a module status would be a second copy of the rule that decides
 * what a customer is charged for, and `two-copies-of-one-rule` is what happens next. The four
 * opening articles are `planned` today, so on a fresh estate this panel lists them and sells none
 * of them — which is the truth, stated, and not an empty tab reading *"nothing to add"*.
 */

import { useCallback, useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { FactList } from './FactList';
import { ServerRead } from './ServerRead';
import { StateBlock } from './StateBlock';
import { StatusChip } from './StatusChip';
import { callOperation } from '../api/operations';
import type { ModuleOfferView } from '../api/contract';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { Icon, type IconName } from '../shell/Icon';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { useServerData } from '../api/useServerData';

/**
 * WHY AN ARTICLE CANNOT BE BOUGHT, IN HEBREW. The server's closed vocabulary, one sentence each.
 *
 * Listed rather than interpolated for the reason `TenantBillingScreen`'s refusal map gives: a
 * computed key does not compile, so a missing translation would be a runtime blank instead of a
 * build failure. A fourth word the server grows later falls through to the uniform wording.
 */
const BLOCK_KEYS: Readonly<Record<string, TextKey>> = {
  not_for_sale: 'store.block.not_for_sale',
  module_not_ready: 'store.block.module_not_ready',
  unpriced: 'store.block.unpriced',
};

/**
 * THE SEVEN REFUSALS THE WRITE CAN ANSWER WITH, each its own sentence.
 *
 * `already_held` and `module_not_ready` are different things for a person to do — one is *"you
 * already have it"* and the other is *"it is not built yet"* — and the three estate-level ones
 * (`no_price_book`, `not_written`, `no_subscription`) are ours to fix and say so. Collapsing them
 * into *"the purchase failed"* is the defect this map exists to prevent.
 */
const REFUSAL_KEYS: Readonly<Record<string, TextKey>> = {
  no_subscription: 'store.refusal.no_subscription',
  no_price_book: 'store.refusal.no_price_book',
  unknown_sku: 'store.refusal.unknown_sku',
  already_held: 'store.refusal.already_held',
  not_for_sale: 'store.refusal.not_for_sale',
  module_not_ready: 'store.refusal.module_not_ready',
  unpriced: 'store.refusal.unpriced',
  not_written: 'store.refusal.not_written',
};

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WHAT EACH MODULE CARD SAYS, AND THE PICTURE BESIDE IT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The Owner, 2026-11-02, on the first draft of this card:
 *
 *     *"אל תעשה את זה. שלחתי לך תמונה של הוספת CRM באייקאונט, בבקשה תעשה אותו דבר — כל הנתונים
 *      היבשים האלו לא מעניינים."*
 *
 * He is right, and the screenshot he sent back is the evidence: `כלול: CFO`, then three figures
 * in a row. That is a RECORD of an article. A person deciding whether to add something to their
 * practice is not reading a record — they are reading about a product, which is what the flow he
 * pointed at shows: a picture, a headline, one sentence of what it does for you, three lines of
 * what that means, and the price.
 *
 * ── KEYED BY MODULE AND LISTED, NOT INTERPOLATED ──────────────────────────────────────────────
 *
 * `t()` takes a member of the catalogue's literal union, so a computed key does not compile —
 * which is the property that makes a missing translation a BUILD failure instead of a blank line
 * on a customer's screen. The same reason the refusals above are a listed map.
 *
 * A module with no entry is not a defect and renders a complete card: the generic lead, and what
 * is included. An article licensing SEVERAL modules is a bundle and says so, because a lead
 * sentence about one of four would be describing a quarter of what is for sale.
 */
const MODULE_COPY: Readonly<
  Record<string, { readonly icon: IconName; readonly lead: TextKey; readonly points: readonly TextKey[] }>
> = {
  vat_control: {
    icon: 'shield',
    lead: 'store.lead.vat_control',
    points: ['store.point.vat_control.1', 'store.point.vat_control.2', 'store.point.vat_control.3'],
  },
  bank_statements_admin: {
    icon: 'building',
    lead: 'store.lead.bank_statements_admin',
    points: [
      'store.point.bank_statements_admin.1',
      'store.point.bank_statements_admin.2',
      'store.point.bank_statements_admin.3',
    ],
  },
  credit_cards: {
    icon: 'credit-card',
    lead: 'store.lead.credit_cards',
    points: ['store.point.credit_cards.1', 'store.point.credit_cards.2', 'store.point.credit_cards.3'],
  },
  collection: {
    icon: 'repeat',
    lead: 'store.lead.collection',
    points: ['store.point.collection.1', 'store.point.collection.2', 'store.point.collection.3'],
  },
  cfo2: {
    icon: 'trending-down',
    lead: 'store.lead.cfo2',
    points: ['store.point.cfo2.1', 'store.point.cfo2.2', 'store.point.cfo2.3'],
  },
  financial_reports: {
    icon: 'chart-column',
    lead: 'store.lead.financial_reports',
    points: [
      'store.point.financial_reports.1',
      'store.point.financial_reports.2',
      'store.point.financial_reports.3',
    ],
  },
};

/** What happened to the one article a person pressed. Never a panel-wide flag — see `#buy`. */
type Buying =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly skuCode: string }
  | {
      readonly kind: 'bought';
      readonly skuCode: string;
      /* CAPTURED AT THE CLICK. By the time this renders, the card that held the name is gone. */
      readonly displayNameHe: string;
      readonly effectiveFrom: string;
      readonly firstInvoicePeriodEndsOn: string | null;
    }
  | { readonly kind: 'refused'; readonly skuCode: string; readonly reason: string }
  | { readonly kind: 'failed'; readonly skuCode: string; readonly failure: ActionFailure };

export function ModuleStorePanel(): JSX.Element {
  const { t } = useLocale();
  const readOffers = useCallback(() => callOperation('tenant.module_offer.read', {}), []);
  const offers = useServerData(readOffers);
  /*
   * ── THE OUTCOME IS HELD HERE, ABOVE THE READ IT INVALIDATES ───────────────────────────────
   *
   * The same measured mistake as the cancellation tab: a bought article disappears from the next
   * read — an article the office HOLDS is not an offer — so `reload()` unmounts the card that was
   * pressed. State inside that card would go with it and the confirmation would flash and vanish.
   */
  const [buying, setBuying] = useState<Buying>({ kind: 'idle' });

  const buy = useCallback(
    (skuCode: string, displayNameHe: string) => {
      setBuying({ kind: 'sending', skuCode });
      void callOperation('tenant.module.purchase', { body: { skuCode } })
        .then((done) => {
          setBuying({
            kind: 'bought',
            skuCode,
            displayNameHe,
            effectiveFrom: done.effectiveFrom,
            firstInvoicePeriodEndsOn: done.firstInvoicePeriodEndsOn,
          });
          /* The list is now wrong in a specific way: it still offers what the office just got. */
          offers.reload();
        })
        .catch((error: unknown) => {
          const failure = classifyActionError(error);
          /*
           * A 422 IS A SENTENCE AND NOT A REFERENCE CODE. `ActionFailureNotice` renders the
           * server's `remedy` for a refusal, and this route sends the WORD rather than Hebrew
           * prose — the words belong in the catalogue, where they have an English twin and a
           * missing one fails the build. So the named refusals are kept here and everything else
           * (denied, step-up, conflict, unreachable) goes to the shared notice unchanged.
           */
          if (failure.kind === 'refused') {
            setBuying({ kind: 'refused', skuCode, reason: failure.reason });
            return;
          }
          setBuying({ kind: 'failed', skuCode, failure });
        });
    },
    [offers],
  );

  return (
    <ServerRead result={offers.result} whatHe={t('store.reading')} skeletonRows={4}>
      {(data) => (
        <>
          {/*
            THE ESTATE'S OWN STATE, SAID ONCE. `priceBookAvailable: false` means nothing can be
            bought and no article is at fault; printing `unpriced` beside every row would blame the
            catalogue for something the platform has not set up.
          */}
          {data.priceBookAvailable ? null : (
            <StateBlock
              titleHe={t('store.noPriceBook')}
              bodyHe={t('store.noPriceBookBody')}
              tone="unavailable"
            />
          )}

          {/*
            A WARNING AND NOT A GATE. The purchase charges nothing now, so a missing card does not
            stop it — but the invoice it lands on will need one, and the person reading this panel
            is the person who can add it. `refuse-in-the-right-order`: this is said about the
            INVOICE, not as a reason the office may not buy.
          */}
          {data.paymentMethodOnFile ? null : (
            <StateBlock
              titleHe={t('store.noCard')}
              bodyHe={t('store.noCardBody')}
              tone="unavailable"
            />
          )}

          {/*
            ══ THE CONFIRMATION IS HERE AND NOT IN THE CARD. MEASURED, 2026-11-02. ═══════════════

            It was inside `OfferCard`, and in the rendered harness it APPEARED AND VANISHED: the
            purchase succeeds, `reload()` runs, and the next read legitimately no longer offers an
            article the office now HOLDS — so React unmounts the card, and the confirmation went
            with it. The office activated a module and the screen said nothing.

            NO JSDOM CASE COULD HAVE SEEN IT. A stubbed read answers the same list every time, so
            the card never left and the assertion passed. It took the browser, against a harness
            that keeps what was bought — and the case that now covers it answers differently on
            the second read, which is what `StubbedRoute.answer` exists for.

            The cancellation tab's own header records the identical mistake on the identical
            screen. Twice is the second copy of one lesson: state belongs ABOVE the read that
            invalidates it.
          */}
          {buying.kind === 'bought' ? (
            <StateBlock
              titleHe={`${t('store.bought')} — ${buying.displayNameHe}`}
              bodyHe={t('store.boughtBody')}
              nextStep={
                <FactList
                  facts={[
                    {
                      labelHe: t('store.boughtFrom'),
                      value: <span dir="ltr">{buying.effectiveFrom}</span>,
                    },
                    {
                      labelHe: t('store.boughtProrated'),
                      value:
                        buying.firstInvoicePeriodEndsOn === null ? (
                          t('store.periodUnknown')
                        ) : (
                          <span dir="ltr">{buying.firstInvoicePeriodEndsOn}</span>
                        ),
                    },
                  ]}
                />
              }
            />
          ) : null}

          {data.offers.length === 0 ? (
            <StateBlock titleHe={t('store.empty')} bodyHe={t('store.emptyBody')} tone="empty" />
          ) : (
            /*
              A GRID AND NOT A STACK. The reference flow lays the modules out as cards side by
              side, which is also what makes the page readable: four stacked panels is four screens
              of scrolling to compare two prices. `auto-fit` over a floor decides how many fit at
              this width, exactly as the home screen's work column does — nothing here picks a
              column count, so it is one column on a phone without a second rule saying so.
            */
            <div className="gp-store-grid">
              {data.offers.map((offer) => (
                <OfferCard
                  key={offer.skuCode}
                  offer={offer}
                  periodEndsOn={data.currentPeriodEndsOn}
                  buying={buying.kind !== 'idle' && buying.skuCode === offer.skuCode ? buying : null}
                  onBuy={buy}
                />
              ))}
            </div>
          )}
        </>
      )}
    </ServerRead>
  );
}

/**
 * ONE MODULE'S CARD — a picture, a headline, what it does, what that means, the price, one button.
 *
 * THE SHAPE IS THE OWNER'S REFERENCE FLOW and the order is deliberate: a person reads what it is
 * before what it costs, and reaches the button last. The first draft put `כלול: CFO` and three
 * figures where the sentence belongs, and he sent it back.
 *
 * `buying` IS THIS CARD'S OR NULL. The panel holds one outcome for the whole list and passes it
 * only to the card it belongs to, so pressing הפעלה on one module never disables or confirms
 * another. A single boolean would have done exactly that.
 */
function OfferCard({
  offer,
  periodEndsOn,
  buying,
  onBuy,
}: {
  readonly offer: ModuleOfferView;
  readonly periodEndsOn: string | null;
  readonly buying: Exclude<Buying, { kind: 'idle' }> | null;
  readonly onBuy: (skuCode: string, displayNameHe: string) => void;
}): JSX.Element {
  const { t } = useLocale();
  const blockKey = offer.blockedBy === null ? null : BLOCK_KEYS[offer.blockedBy];

  /*
   * ONE MODULE OR SEVERAL, AND THEY ARE NOT THE SAME CARD. A single-module article speaks about
   * that module; a bundle says it is a bundle and lists what is in it. `moduleCodes` is the
   * server's, so neither sentence is this file deciding what was sold.
   */
  const only = offer.moduleCodes.length === 1 ? MODULE_COPY[offer.moduleCodes[0] ?? ''] : undefined;
  const bundle = offer.moduleCodes.length > 1;
  const icon: IconName = only?.icon ?? (bundle ? 'layers' : 'package');
  const lead: TextKey = only?.lead ?? (bundle ? 'store.lead.bundle' : 'store.lead.generic');

  return (
    <section className="gp-panel gp-store-card" data-available={offer.purchasable ? 'yes' : 'no'}>
      <div className="gp-store-card__head">
        <span className="gp-overview-icon">
          <Icon name={icon} />
        </span>
        <h3>{offer.displayNameHe}</h3>
      </div>

      <p className="gp-store-card__lead">{t(lead)}</p>

      {/*
        WHAT THAT MEANS, IN THREE LINES. For a single module they are its own; for a bundle they
        are the modules it carries, by their Hebrew names from `core.module` — never the codes,
        which are identifiers and not products.
      */}
      <ul className="gp-store-card__points">
        {only === undefined
          ? offer.moduleNamesHe.map((name) => <li key={name}>{name}</li>)
          : only.points.map((point) => <li key={point}>{t(point)}</li>)}
      </ul>

      {/*
        THE PRICE, AS THE SERVER FORMATTED IT — `100.00 ₪` and never `390.0000 ILS`. `dir="ltr"`
        because a shekel figure inside an RTL paragraph is placed back to front otherwise.

        AND NO FIGURE AT ALL WHEN THERE IS NONE. Not `0.00 ₪`: a zero would claim the module is
        free, and the chip below already says what is true.
      */}
      <div className="gp-store-card__price">
        {offer.unitPriceDisplay === null ? (
          <span className="gp-store-card__noprice">{t('store.noPrice')}</span>
        ) : (
          <>
            <span className="gp-store-card__amount" dir="ltr">
              {offer.unitPriceDisplay}
            </span>
            <span className="gp-store-card__per">
              {t('store.perMonth')} · {t('store.exVat')}
            </span>
            <span className="gp-store-card__gross">
              {offer.totalDisplay === null ? (
                /* A price with no rate in force. The ex-VAT figure is true; a gross one would not be. */
                t('store.vatUnknown')
              ) : (
                <>
                  <span dir="ltr">{offer.totalDisplay}</span> {t('store.withVat')}
                </>
              )}
            </span>
          </>
        )}
      </div>

      {blockKey === undefined ? (
        /* A block word this build has no sentence for. Said plainly rather than left blank. */
        <StatusChip labelHe={t('store.block.other')} tone="muted" />
      ) : blockKey === null ? null : (
        <StatusChip labelHe={t(blockKey)} tone="muted" />
      )}

      {buying?.kind === 'refused' ? (
        <StateBlock
          titleHe={t('store.refusedTitle')}
          bodyHe={t(REFUSAL_KEYS[buying.reason] ?? 'store.refusal.other')}
          tone="unavailable"
        />
      ) : null}

      {buying?.kind === 'failed' ? <ActionFailureNotice failure={buying.failure} /> : null}

      <div className="gp-store-card__foot">
        <button
          type="button"
          className="btn btn--primary"
          /*
           * DISABLED ONLY FOR WHAT THE SERVER REFUSED OR WHAT IS IN FLIGHT, and NOT after a
           * refusal: a refusal the office can act on — adding a card, say — must leave the button
           * pressable, or the person has to reload the screen to retry something we have just
           * told them how to fix. A bought module's button stays out of reach because the card
           * itself is about to leave the list.
           */
          disabled={!offer.purchasable || buying?.kind === 'sending' || buying?.kind === 'bought'}
          onClick={() => onBuy(offer.skuCode, offer.displayNameHe)}
        >
          {buying?.kind === 'sending' ? t('store.activating') : t('store.activate')}
        </button>
        {/* The sentence that makes *"קונה מיד"* true about money as well as about access. */}
        {offer.purchasable && periodEndsOn !== null ? (
          <p className="gp-store-card__note">
            {t('store.prorationNote')} <span dir="ltr">{periodEndsOn}</span>
          </p>
        ) : null}
      </div>
    </section>
  );
}
